import { createAsyncThunk, GetThunkAPI } from '@reduxjs/toolkit';
import { BaseService } from './base-service';

export class BaseThunk<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
    constructor(private service: BaseService<T, TCreate, TUpdate>) {}

    getAll<TPayload = ResponseBase<T[]>, TParams = any>(
        type: string,
        customParamsBuilder?: (params: TParams, thunkAPI: GetThunkAPI<any>) => any,
    ) {
        return createAsyncThunk<TPayload, TParams, { rejectValue: string }>(
            type,
            async (params: TParams, thunkAPI) => {
                try {
                    const finalParams = customParamsBuilder
                        ? customParamsBuilder(params, thunkAPI)
                        : params;

                    const res = await this.service.getAll(finalParams);
                    if (!res.success) {
                        return thunkAPI.rejectWithValue(res.error);
                    }
                    return res.data as TPayload;
                } catch (error: any) {
                    return thunkAPI.rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
    getById<TPayload = T>(type: string) {
        return createAsyncThunk<TPayload, string, { rejectValue: string }>(
            type,
            async (id, { rejectWithValue }) => {
                try {
                    const result = await this.service.getById(id);
                    return result as TPayload;
                } catch (error: any) {
                    return rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
    create<TPayload = T>(type: string) {
        return createAsyncThunk<TPayload, TCreate, { rejectValue: string }>(
            type,
            async (data, { rejectWithValue }) => {
                try {
                    const result = await this.service.create(data);
                    return result as TPayload;
                } catch (error: any) {
                    return rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
    update<TPayload = T>(type: string) {
        return createAsyncThunk<TPayload, { id: string; data: TUpdate }, { rejectValue: string }>(
            type,
            async ({ id, data }, { rejectWithValue }) => {
                try {
                    const result = await this.service.update(id, data);
                    return result as TPayload;
                } catch (error: any) {
                    return rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
    deleteAll<TPayload = T[]>(type: string) {
        return createAsyncThunk<TPayload, string[], { rejectValue: string }>(
            type,
            async (ids, { rejectWithValue }) => {
                try {
                    const result = await this.service.deleteAll(ids);
                    return result as TPayload;
                } catch (error: any) {
                    return rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
    deleteById(type: string) {
        return createAsyncThunk<void, string, { rejectValue: string }>(
            type,
            async (id, { rejectWithValue }) => {
                try {
                    await this.service.deleteById(id);
                } catch (error: any) {
                    return rejectWithValue(error.message || 'Unexpected error');
                }
            },
        );
    }
}
